/**
 * src/components/authority/AuthorityPanel.tsx
 *
 * The read-only answer to "why is that button greyed out". Renders the
 * whole authorization response for one wallet on one object: where its
 * authority comes from, what each permission resolves to, and the sentence
 * explaining every refusal.
 *
 * ## Why the panel shows everything rather than a summary
 *
 * It exists to be argued with. When an operator says "I own this, why can't
 * I edit it", the useful reply is not "you are not authorized" but the
 * specific chain of facts — the object is suspended, or the binding is
 * proposed rather than approved, or the organization's authority
 * verification was withdrawn. Collapsing that to a badge would remove the
 * only thing the panel is for.
 *
 * ## Refused and unanswered are drawn differently
 *
 * A permission that is false because the chain said no is the system
 * working. A permission that is false because a contract read failed is a
 * deployment problem wearing the same clothes. Both deny, which is correct,
 * but only one is worth paging someone about, so they get different marks
 * and the unread calls are named in a banner.
 *
 * Styles are inline and self-contained for the same reason as
 * `bindingUi.tsx`: this renders inside two hosts with incompatible CSS
 * conventions.
 */

import React from 'react';
import {
  permissionRows,
  describeAuthority,
  describeDegradation,
  describeAdvisory,
  isLegacyOnly,
  SOURCE_LABEL,
  SOURCE_EXPLANATION,
  type ObjectAuthorization,
  type OrganizationAuthorization,
  type PermissionRow,
  type AuthoritySource,
} from '../../v2/services/authorizationModel';
import { RELATIONSHIP_LABEL, type RelationshipName } from '../../v2/services/spatialRegistryService';
import { Pill } from '../spatial-bindings/bindingUi';

// ── Layout ───────────────────────────────────────────────────────────────────

const shell: React.CSSProperties = {
  border: '1px solid #e3e6ea',
  borderRadius: '6px',
  padding: '12px',
  backgroundColor: '#fbfcfd',
  fontSize: '12px',
  color: '#333',
};

const heading: React.CSSProperties = {
  fontSize: '12px', fontWeight: 700, margin: '0 0 8px', color: '#222',
};

const groupTitle: React.CSSProperties = {
  fontSize: '11px', fontWeight: 700, textTransform: 'uppercase',
  letterSpacing: '0.04em', color: '#7a828a', margin: '10px 0 4px',
};

const row: React.CSSProperties = {
  display: 'flex', gap: '8px', alignItems: 'baseline', padding: '2px 0',
};

const term: React.CSSProperties = { color: '#6c757d', minWidth: '132px', flexShrink: 0 };

const value: React.CSSProperties = { fontWeight: 600, wordBreak: 'break-word' };

const banner = (tone: 'warn' | 'bad' | 'info'): React.CSSProperties => ({
  padding: '8px 10px',
  borderRadius: '4px',
  marginBottom: '8px',
  fontSize: '11px',
  lineHeight: 1.45,
  backgroundColor: tone === 'bad' ? '#f8d7da' : tone === 'warn' ? '#fff3cd' : '#d9e9ff',
  color: tone === 'bad' ? '#721c24' : tone === 'warn' ? '#856404' : '#0b4a8f',
  border: `1px solid ${tone === 'bad' ? '#f1b0b7' : tone === 'warn' ? '#ffe28a' : '#b6d4fe'}`,
});

const mono: React.CSSProperties = { fontFamily: 'monospace', fontSize: '11px' };

// ── Pieces ───────────────────────────────────────────────────────────────────

const Fact: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div style={row}>
    <span style={term}>{label}</span>
    <span style={value}>{children}</span>
  </div>
);

const YesNo: React.FC<{ value: boolean }> = ({ value: v }) => (
  <Pill tone={v ? 'good' : 'neutral'}>{v ? 'Yes' : 'No'}</Pill>
);

/**
 * One permission line.
 *
 * Three marks rather than two. `✓` granted, `✗` refused, `?` could not be
 * determined — because a UI that shows an unreadable permission as a plain
 * refusal hides the fact that something is broken.
 */
const PermissionLine: React.FC<{ row: PermissionRow }> = ({ row: r }) => {
  const mark = r.allowed ? '✓' : r.unknown ? '?' : '✗';
  const colour = r.allowed ? '#1a7f37' : r.unknown ? '#8a6d00' : '#b42318';
  return (
    <div style={{ ...row, alignItems: 'flex-start' }} title={r.reason || undefined}>
      <span style={{ color: colour, fontWeight: 700, width: '14px', flexShrink: 0 }}>{mark}</span>
      <span style={{ minWidth: '150px', flexShrink: 0, fontWeight: r.allowed ? 600 : 400 }}>
        {r.label}
      </span>
      {!r.allowed && r.reason && (
        <span style={{ color: '#6c757d', fontWeight: 400, lineHeight: 1.4 }}>{r.reason}</span>
      )}
    </div>
  );
};

const SourceList: React.FC<{ sources: AuthoritySource[] }> = ({ sources }) => {
  if (!sources.length) {
    return <span style={{ color: '#6c757d', fontWeight: 400 }}>None recorded</span>;
  }
  return (
    <span style={{ display: 'inline-flex', gap: '4px', flexWrap: 'wrap' }}>
      {sources.map((s) => (
        <Pill
          key={s}
          tone={s === 'creator_legacy' ? 'warn' : 'info'}
          title={SOURCE_EXPLANATION[s]}
        >
          {SOURCE_LABEL[s] ?? s}
        </Pill>
      ))}
    </span>
  );
};

const shortId = (id: string | null | undefined): string =>
  id ? `${id.slice(0, 8)}…${id.slice(-6)}` : '—';

// ── Object authority ─────────────────────────────────────────────────────────

export interface AuthorityPanelProps {
  auth: ObjectAuthorization | null;
  loading?: boolean;
  error?: string | null;
  wallet?: string | null;
  /** Rendered collapsed by default; this is a debugging view, not a headline. */
  defaultOpen?: boolean;
  /** Falls back to a generic title when absent. */
  objectName?: string | null;
}

/**
 * The authority readout for one spatial object.
 *
 * Note the deliberate juxtaposition near the top: an administrative
 * authority such as a country admin routinely shows "Manage object ✗"
 * beside "Suspend ✓". That is not an inconsistency to smooth over — it is
 * the point. Being able to intervene in what an object is does not make you
 * the person who runs it.
 */
export const AuthorityPanel: React.FC<AuthorityPanelProps> = ({
  auth, loading = false, error = null, wallet = null, defaultOpen = false, objectName = null,
}) => {
  const [open, setOpen] = React.useState(defaultOpen);

  const rows = permissionRows(auth);
  const operational = rows.filter((r) => r.kind === 'operational');
  const administrative = rows.filter((r) => r.kind === 'administrative');
  const mixed = rows.filter((r) => r.kind === 'mixed');

  const degradation = describeDegradation(auth);
  const advisory = describeAdvisory(auth?.advisory);
  const ctx = auth?.context;
  const org = ctx?.organization;

  return (
    <div style={shell}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h5 style={{ ...heading, marginBottom: open ? '8px' : 0 }}>
          Authority{objectName ? ` — ${objectName}` : ''}
        </h5>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          style={{
            background: 'none', border: 'none', color: '#007bff',
            cursor: 'pointer', fontSize: '11px', padding: 0,
          }}
        >
          {open ? 'Hide' : 'Show'}
        </button>
      </div>

      {!open && (
        <div style={{ color: '#6c757d', fontSize: '11px' }}>
          {loading ? 'Resolving…' : describeAuthority(auth)}
        </div>
      )}

      {open && (
        <>
          {loading && (
            <div style={{ color: '#6c757d', marginBottom: '8px' }}>
              Reading authority from the chain…
            </div>
          )}

          {error && (
            <div style={banner('bad')}>
              Authorization could not be resolved: {error}. Every action is treated as
              unavailable until it can be, rather than assumed allowed.
            </div>
          )}

          {!loading && !error && !auth && (
            <div style={banner('info')}>
              {wallet
                ? 'No authorization has been resolved for this object.'
                : 'Connect a wallet to see what it may do here.'}
            </div>
          )}

          {degradation && <div style={banner('warn')}>{degradation}</div>}
          {advisory && <div style={banner(auth?.advisory.denied ? 'bad' : 'warn')}>{advisory}</div>}

          {auth && isLegacyOnly(auth) && (
            <div style={banner('warn')}>
              The only authority behind this is the wallet recorded as the creator in
              GeoLink. Register the object and bind it to an organization to decide this
              on chain instead.
            </div>
          )}

          {auth && (
            <>
              <div style={groupTitle}>Ownership</div>
              <Fact label="Registered on chain"><YesNo value={auth.registered} /></Fact>
              {ctx?.organization ? (
                <>
                  <Fact label="Owning organization">
                    <span style={mono}>{shortId(ctx.effectiveOrganizationId)}</span>
                    {ctx.inherited && (
                      <>
                        {' '}
                        <Pill tone="info" title="Resolved from an ancestor object, not from a binding on this object.">
                          Inherited
                        </Pill>
                      </>
                    )}
                  </Fact>
                  {ctx.binding?.relationshipName && (
                    <Fact label="Relationship">
                      {RELATIONSHIP_LABEL[ctx.binding.relationshipName as RelationshipName]
                        ?? ctx.binding.relationshipName}
                    </Fact>
                  )}
                  <Fact label="Binding">
                    <Pill tone={ctx.binding?.statusName === 'approved' ? 'good' : 'warn'}>
                      {ctx.binding?.statusName ?? 'none'}
                    </Pill>
                  </Fact>
                  <Fact label="Object status">
                    <Pill tone={ctx.statusName === 'active' ? 'good' : 'warn'}>
                      {ctx.statusName}
                    </Pill>
                  </Fact>
                  <Fact label="Organization status">
                    <Pill tone={org?.statusName === 'active' ? 'good' : 'warn'}>
                      {org?.statusName ?? 'unknown'}
                    </Pill>
                  </Fact>
                  <Fact label="Identity verified">
                    <YesNo value={!!org?.verificationV2?.identityVerified} />
                  </Fact>
                  <Fact label="Authority verified">
                    <YesNo value={!!org?.verificationV2?.authorityVerified} />
                  </Fact>
                  <Fact label="Beneficiary verified">
                    <YesNo value={!!org?.verificationV2?.beneficiaryVerified} />
                  </Fact>
                </>
              ) : (
                <Fact label="Owning organization">
                  <span style={{ color: '#6c757d', fontWeight: 400 }}>
                    {auth.registered
                      ? 'None — no approved binding on this object or any ancestor'
                      : 'Not registered in the spatial registry'}
                  </span>
                </Fact>
              )}

              <div style={groupTitle}>This wallet</div>
              <Fact label="Wallet">
                <span style={mono}>{shortId(auth.wallet)}</span>
              </Fact>
              <Fact label="Authority from">
                <SourceList sources={auth.authoritySources} />
              </Fact>
              {ctx && ctx.grant.mask !== 0 && (
                <Fact label="Delegated grant">
                  <span style={{ display: 'inline-flex', gap: '4px' }}>
                    {ctx.grant.manageObject && <Pill tone="info">MANAGE_OBJECT</Pill>}
                    {ctx.grant.manageRules && <Pill tone="info">MANAGE_RULES</Pill>}
                    {ctx.grant.manageFunds && <Pill tone="info">MANAGE_FUNDS</Pill>}
                  </span>
                </Fact>
              )}

              <div style={groupTitle}>Operational — may run the object</div>
              {operational.map((r) => <PermissionLine key={r.permission} row={r} />)}

              <div style={groupTitle}>Administrative — may intervene in the object</div>
              {administrative.map((r) => <PermissionLine key={r.permission} row={r} />)}

              <div style={groupTitle}>Either route</div>
              {mixed.map((r) => <PermissionLine key={r.permission} row={r} />)}

              {ctx && (
                <>
                  <div style={groupTitle}>Payout</div>
                  <Fact label="Beneficiary">
                    {ctx.beneficiary
                      ? <span style={mono}>{shortId(ctx.beneficiary)}</span>
                      : <span style={{ color: '#6c757d', fontWeight: 400 }}>Not set</span>}
                  </Fact>
                  <Fact label="May receive value">
                    <YesNo value={ctx.canObjectReceive} />
                  </Fact>
                  {ctx.pendingTransfer && (
                    <Fact label="Pending reassignment">
                      <Pill tone="warn">
                        {shortId(ctx.pendingTransfer.fromOrg)} → {shortId(ctx.pendingTransfer.toOrg)}
                      </Pill>
                    </Fact>
                  )}
                </>
              )}

              <div
                style={{
                  marginTop: '10px', paddingTop: '8px', borderTop: '1px solid #e9ecef',
                  color: '#6c757d', fontSize: '11px', lineHeight: 1.45,
                }}
              >
                Read from the contracts, not from GeoLink metadata. A country admin will
                normally show operational permissions as refused and administrative ones
                as allowed — intervening in what an object is and running it day to day
                are separate authorities.
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

// ── Organization authority ───────────────────────────────────────────────────

export interface OrganizationAuthorityPanelProps {
  auth: OrganizationAuthorization | null;
  loading?: boolean;
  error?: string | null;
  wallet?: string | null;
  organizationName?: string | null;
  defaultOpen?: boolean;
}

/**
 * The same readout for an organization: who may administer the record, and
 * what this wallet is inside it.
 *
 * The two are kept apart because they are genuinely different. Only a
 * country admin may verify or suspend an organization; only its own admins
 * and operators can act on its objects. A wallet can easily be one and not
 * the other.
 */
export const OrganizationAuthorityPanel: React.FC<OrganizationAuthorityPanelProps> = ({
  auth, loading = false, error = null, wallet = null, organizationName = null, defaultOpen = false,
}) => {
  const [open, setOpen] = React.useState(defaultOpen);
  const degradation = describeDegradation(auth);
  const advisory = describeAdvisory(auth?.advisory);

  return (
    <div style={shell}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h5 style={{ ...heading, marginBottom: open ? '8px' : 0 }}>
          Authority{organizationName ? ` — ${organizationName}` : ''}
        </h5>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          style={{
            background: 'none', border: 'none', color: '#007bff',
            cursor: 'pointer', fontSize: '11px', padding: 0,
          }}
        >
          {open ? 'Hide' : 'Show'}
        </button>
      </div>

      {!open && (
        <div style={{ color: '#6c757d', fontSize: '11px' }}>
          {loading ? 'Resolving…' : describeAuthority(auth)}
        </div>
      )}

      {open && (
        <>
          {loading && (
            <div style={{ color: '#6c757d', marginBottom: '8px' }}>
              Reading authority from the chain…
            </div>
          )}

          {error && (
            <div style={banner('bad')}>
              Authorization could not be resolved: {error}. Actions are treated as
              unavailable rather than assumed allowed.
            </div>
          )}

          {!loading && !error && !auth && (
            <div style={banner('info')}>
              {wallet
                ? 'This organization is not registered on chain, so there is no authority to report.'
                : 'Connect a wallet to see what it may do here.'}
            </div>
          )}

          {degradation && <div style={banner('warn')}>{degradation}</div>}
          {advisory && <div style={banner(auth?.advisory.denied ? 'bad' : 'warn')}>{advisory}</div>}

          {auth && (
            <>
              <div style={groupTitle}>Organization</div>
              <Fact label="Country">{auth.alpha2 ?? auth.numericCode ?? '—'}</Fact>
              <Fact label="Status">
                <Pill tone={auth.statusName === 'active' ? 'good' : 'warn'}>{auth.statusName}</Pill>
              </Fact>
              <Fact label="Identity verified">
                <YesNo value={!!auth.verificationV2?.identityVerified} />
              </Fact>
              <Fact label="Authority verified">
                <YesNo value={!!auth.verificationV2?.authorityVerified} />
              </Fact>
              <Fact label="Beneficiary verified">
                <YesNo value={!!auth.verificationV2?.beneficiaryVerified} />
              </Fact>
              <Fact label="May own objects">
                <YesNo value={auth.capabilities.canOwnObjects} />
              </Fact>
              <Fact label="May operate objects">
                <YesNo value={auth.capabilities.canOperateObjects} />
              </Fact>
              <Fact label="May receive funds">
                <YesNo value={auth.capabilities.canReceiveFunds} />
              </Fact>

              <div style={groupTitle}>This wallet</div>
              <Fact label="Wallet"><span style={mono}>{shortId(auth.wallet)}</span></Fact>
              <Fact label="Authority from">
                <SourceList sources={auth.authoritySources} />
              </Fact>
              <Fact label="Organization admin"><YesNo value={auth.isOrganizationAdmin} /></Fact>
              <Fact label="Organization operator"><YesNo value={auth.isOrganizationOperator} /></Fact>
              <Fact label="Approved beneficiary"><YesNo value={auth.isApprovedBeneficiary} /></Fact>

              <div style={groupTitle}>May administer the record</div>
              <Fact label="Grant roles"><YesNo value={auth.canGrantRoles} /></Fact>
              <Fact label="Set verification"><YesNo value={auth.canVerify} /></Fact>
              <Fact label="Set status"><YesNo value={auth.canSetStatus} /></Fact>

              <div
                style={{
                  marginTop: '10px', paddingTop: '8px', borderTop: '1px solid #e9ecef',
                  color: '#6c757d', fontSize: '11px', lineHeight: 1.45,
                }}
              >
                Administering the organization record is country-scoped. Being an admin or
                operator <em>of</em> the organization is what carries authority over its
                objects — the two do not imply each other.
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default AuthorityPanel;
